/**
 * Central registry of TanStack Query keys. Co-locating them keeps cache
 * invalidation cheap: a mutation that needs to bust the transaction list
 * just calls `queryClient.invalidateQueries({ queryKey: qk.transactions.all })`.
 */
export const qk = {
  funds: {
    all: ['funds'] as const,
    list: (opts: { includeInactive?: boolean } = {}) =>
      ['funds', 'list', { includeInactive: opts.includeInactive ?? false }] as const,
    detail: (id: string) => ['funds', 'detail', id] as const,
  },
  subHeads: {
    all: ['sub-heads'] as const,
    listByFund: (fundId: string, opts: { includeInactive?: boolean } = {}) =>
      ['sub-heads', 'by-fund', fundId, { includeInactive: opts.includeInactive ?? false }] as const,
  },
  transactions: {
    all: ['transactions'] as const,
    list: (query: Record<string, unknown>) => ['transactions', 'list', query] as const,
    detail: (id: string) => ['transactions', 'detail', id] as const,
  },
  budgets: {
    all: ['budgets'] as const,
    /**
     * Loose `query` type so the same factory accepts both the rich
     * `ListBudgetsOptions` shape (fyYear/fundId) and ad-hoc invalidation
     * keys. The TanStack cache only ever serialises this for equality,
     * so accepting `object` keeps the call-sites cast-free.
     */
    list: (query: object) => ['budgets', 'list', query] as const,
    vsActual: (fundId: string, fyYear: number) => ['budgets', 'vs-actual', fundId, fyYear] as const,
    burndown: (id: string) => ['budgets', 'burndown', id] as const,
  },
  audit: {
    all: ['audit-log'] as const,
    list: (query: Record<string, unknown>) => ['audit-log', 'list', query] as const,
  },
  reviews: {
    all: ['reviews'] as const,
    list: (query: object) => ['reviews', 'list', query] as const,
    openCount: () => ['reviews', 'open-count'] as const,
  },
  fdr: {
    all: ['fdr'] as const,
    list: (query: object) => ['fdr', 'list', query] as const,
    detail: (id: string) => ['fdr', 'detail', id] as const,
    summary: () => ['fdr', 'summary'] as const,
    growth: (months: number) => ['fdr', 'growth', months] as const,
    upcoming: (days: number) => ['fdr', 'upcoming', days] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    summary: () => ['dashboard', 'summary'] as const,
    fundSpotlight: (fundCode: string) => ['dashboard', 'spotlight', fundCode] as const,
    monthlyFlow: (fundId: string, months: number) =>
      ['dashboard', 'monthly-flow', fundId, months] as const,
    subHeadSpend: (fundId: string, fyYear: number) =>
      ['dashboard', 'subhead-spend', fundId, fyYear] as const,
    budgetVsActual: (fundId: string, fyYear: number) =>
      ['dashboard', 'budget-vs-actual', fundId, fyYear] as const,
    battalionPulse: () => ['dashboard', 'battalion-pulse'] as const,
  },
  aiKnowledge: {
    all: ['ai-knowledge'] as const,
    active: () => ['ai-knowledge', 'active'] as const,
    list: (query: object) => ['ai-knowledge', 'list', query] as const,
  },
  coOrders: {
    all: ['co-orders'] as const,
    status: () => ['co-orders', 'status'] as const,
    list: (query: object) => ['co-orders', 'list', query] as const,
  },
  auth: {
    registrations: () => ['auth', 'registrations'] as const,
  },
  people: {
    all: ['people'] as const,
    list: () => ['people', 'list'] as const,
    details: () => ['people', 'details'] as const,
  },
  boards: {
    all: ['boards'] as const,
    list: (opts: object = {}) => ['boards', 'list', opts] as const,
    snapshot: (id: string) => ['boards', 'snapshot', id] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
    comments: (taskId: string, query: object = {}) => ['tasks', 'comments', taskId, query] as const,
    myWork: (query: object = {}) => ['tasks', 'my-work', query] as const,
  },
  overview: {
    all: ['overview'] as const,
    boards: () => ['overview', 'boards'] as const,
    workload: () => ['overview', 'workload'] as const,
    pendingUpdates: () => ['overview', 'pending-updates'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (query: object = {}) => ['notifications', 'list', query] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
  },
  inbox: {
    all: ['inbox'] as const,
    list: () => ['inbox', 'list'] as const,
    unreadCount: () => ['inbox', 'unread-count'] as const,
  },
  conversations: {
    all: ['conversations'] as const,
    list: () => ['conversations', 'list'] as const,
    detail: (id: string) => ['conversations', 'detail', id] as const,
    messages: (id: string) => ['conversations', 'messages', id] as const,
    unread: () => ['conversations', 'unread'] as const,
  },
  appointments: {
    all: ['appointments'] as const,
    list: () => ['appointments', 'list'] as const,
  },
  officers: {
    all: ['officers'] as const,
    dossier: (id: string) => ['officers', 'dossier', id] as const,
  },
  taskUpdateFeed: {
    all: ['task-update-feed'] as const,
    list: (query: object = {}) => ['task-update-feed', 'list', query] as const,
    newCount: () => ['task-update-feed', 'new-count'] as const,
  },
} as const;
